import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { useMemo, useRef } from "react";
import { useAccounts } from "@hooks/useAccounts";
import { Entity } from "@common/types/entity";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import { SpreadsheetGrid } from "../../../spreadsheet/SpreadsheetGrid";
import { CellType } from "../../../spreadsheet/cells";
import styles from "./CreateAccountSpreadsheet.module.scss";
import {
    AccountProxyObject,
    ValidationErrors,
} from "./CreateAccountSpreadsheetModal";
import { validateAndTransformAccounts } from "./validateAndTransformAccounts";

interface Props {
    availableClassifications?: AccountClass[];
    accounts: AccountProxyObject[];
    onAccountsChange: (accounts: AccountProxyObject[]) => void;
    validationErrors: ValidationErrors;
    hasAttemptedSubmit: boolean;
    entity: Entity;
}

export const CreateAccountSpreadsheet: React.FC<Props> = ({
    availableClassifications,
    accounts,
    onAccountsChange,
    validationErrors,
    hasAttemptedSubmit,
    entity,
}) => {
    const prevDataRef = useRef(accounts);
    const { accounts: accountsFromHook } = useAccounts({
        entityId: entity.id,
    });
    const { data: chartOfAccountsData } = useChartOfAccountsQuery();
    const groups = useMemo<AccountGroup[]>(
        () => chartOfAccountsData?.groups ?? [],
        [chartOfAccountsData?.groups],
    );

    const columns: ColumnDef[] = useMemo(
        () => [
            {
                field: "name",
                label: "Account Name",
                cellType: CellType.STRING,
                required: true,
                isValid: ({ row }) =>
                    !hasAttemptedSubmit ||
                    !validationErrors.accounts?.[row]?.name,
            },
            {
                field: "accountType",
                label: "Type",
                cellType: CellType.ACCOUNT_TYPE,
                required: true,
                availableClassifications,
                isValid: ({ row }) =>
                    !hasAttemptedSubmit ||
                    !validationErrors.accounts?.[row]?.accountType,
            },
            {
                field: "parentGroupId",
                label: "Group",
                cellType: CellType.ACCOUNT_GROUP,
                required: false,
                groups,
                isValid: () => true,
            },
            {
                field: "mappedKickAccountId",
                label: "Mapped Account",
                cellType: CellType.ACCOUNT_SEARCH,
                accounts: accountsFromHook,
                required: false,
                isValid: ({ row }) =>
                    !hasAttemptedSubmit ||
                    !validationErrors.accounts?.[row]?.mappedKickAccountId,
            },
        ],
        [
            accountsFromHook,
            availableClassifications,
            validationErrors,
            hasAttemptedSubmit,
            groups,
        ],
    );

    const handleChange = (newData: SpreadsheetData) => {
        const prevData = prevDataRef.current;

        const validatedData = validateAndTransformAccounts({
            newData,
            prevData,
            existingAccounts: accountsFromHook,
            groups,
        });

        prevDataRef.current = validatedData;
        onAccountsChange(validatedData);
    };

    return (
        <SpreadsheetGrid
            noOuterBorder
            data={accounts}
            columns={columns}
            onChange={handleChange}
            gridTemplateColumns="1fr 250px 250px 450px"
            rowClassName={styles.spreadsheetRow}
        />
    );
};
